// 계약 관리 — 등록·수정 창(C2b · 칸 16개 + 원본 파일).
// 화면 검사는 서버(backend/routers/contracts.py · services/contract_files.py) 규칙의 사본이다 — 문구를 같게 둔다.
// 막는 것은 서버다(화면이 통과시켜도 400 이면 그 문구가 창 안에 나온다).
// 🔴 수정은 바뀐 칸만 보내고 바뀐 칸(날짜는 바뀐 짝)만 검사한다 — 서버도 보낸 칸만 본다.
//    이관된 옛 행(빈 담당자·뒤집힌 날짜·앞뒤 공백)의 다른 칸 수정이 화면에서 막히지 않게.
import { useEffect, useRef, useState } from 'react';
import { contractsAPI } from '../../api/client';
import { confirmDialog, toast } from '../../components/AppDialog';
import { COLORS, RADIUS, btn, input as inputStyle } from '../../utils/uiStyles';
import {
  CATEGORY_LABELS, FIELD_LABELS, PARTY_LABELS, REGION_LABELS, TERM_LABELS, errorText,
} from './contractLabels';

// 서버 _FIELDS 순서 = 화면 순서 = 검사 순서.
const FIELDS = [
  'contract_no', 'category', 'region', 'party_type', 'term_type', 'contract_name',
  'counterparty', 'center', 'department', 'owner_name', 'sub_owner_name',
  'start_date', 'end_date', 'notice_date', 'auto_renewal', 'summary',
];
// 등록 때 비워도 되는 칸(계약번호는 서버가 채번) · 수정 때 지울 수 있는 칸(서버 _CLEARABLE).
const CREATE_OPTIONAL = new Set(['contract_no', 'center', 'sub_owner_name', 'notice_date', 'summary']);
const CLEARABLE = new Set(['center', 'sub_owner_name', 'notice_date', 'summary']);
const CHOICES = { category: CATEGORY_LABELS, region: REGION_LABELS, party_type: PARTY_LABELS, term_type: TERM_LABELS };
const MAX_LEN = {
  contract_no: 40, contract_name: 300, counterparty: 300, center: 100,
  department: 100, owner_name: 100, sub_owner_name: 100, summary: 20000,
};
const MAX_FILE_BYTES = 20 * 1024 * 1024;
const ALLOWED_EXT = ['pdf', 'doc', 'docx', 'hwp', 'hwpx', 'xls', 'xlsx'];
const FILE_ACCEPT = ALLOWED_EXT.map((ext) => `.${ext}`).join(',');
const EXT_MESSAGE = 'pdf·doc·docx·hwp·hwpx·xls·xlsx 만 올릴 수 있습니다.';
const DATE_ORDER = '종료일이 시작일보다 빠를 수 없습니다.';
const NOTICE_ORDER = '해지통보일이 종료일보다 늦을 수 없습니다.';
const DEPARTMENTS_FAILED = '부서 목록을 읽지 못했습니다. 창을 닫고 다시 열어 주세요.';
const DEPARTMENTS_EMPTY = '등록된 부서가 없어 계약을 등록할 수 없습니다. 계약 관리 → 설정 → 부서에서 먼저 추가해 주세요.';

const fieldId = (name) => `contract-field-${name}`;
const labelStyle = { display: 'block', marginBottom: '6px', color: COLORS.text, fontSize: '13px', fontWeight: 700 };
const boxStyle = { marginBottom: '12px', minWidth: 0 };
const noteStyle = { marginTop: '4px', color: COLORS.textSub, fontSize: '12px' };

// 상세 응답 → 칸 글자. 글자는 다듬지 않고 받은 그대로 둔다(옛 행의 앞뒤 공백 보존) · null 은 빈 글자.
function initialOf(contract) {
  const values = {};
  FIELDS.forEach((name) => {
    if (name === 'auto_renewal') {
      values[name] = contract?.auto_renewal === true;
    } else {
      const raw = contract?.[name];
      values[name] = raw === null || raw === undefined ? '' : String(raw);
    }
  });
  return values;
}

// 보낼 글자 · 비교 글자 — 앞뒤 공백을 뗀다(서버도 뗀다) · 자동연장은 'true'/'false'.
const wireText = (name, value) => (name === 'auto_renewal' ? (value ? 'true' : 'false') : String(value ?? '').trim());

// 서버 contract_files._extension 과 같다 — 마지막 «.» 뒤 · 소문자 · 점이 없으면 빈 글자.
function extensionOf(filename) {
  const base = String(filename || '').trim().replace(/\\/g, '/').split('/').pop();
  const dot = base.lastIndexOf('.');
  return dot < 0 ? '' : base.slice(dot + 1).toLowerCase();
}

export default function ContractFormModal({ mode, contract, departments, departmentsFailed, onClose, onSaved }) {
  const isEdit = mode === 'edit';
  const initialRef = useRef(null);
  if (initialRef.current === null) initialRef.current = initialOf(isEdit ? contract : null);
  const [values, setValues] = useState(() => ({ ...initialRef.current }));
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const savingRef = useRef(false);
  const confirmingRef = useRef(false);
  const mountedRef = useRef(true);
  const firstFieldRef = useRef(null);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    // 연 자리(보통 연 단추)를 첫 칸으로 옮기기 전에 기억한다 — AppDialog 와 같은 판정 · body 는 뺀다.
    const active = document.activeElement;
    returnFocusRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
    // Esc 는 form 에서 받는다 → 포커스가 창 안에 있어야 한다.
    firstFieldRef.current?.focus();
    return () => {
      mountedRef.current = false;
      // 닫히면 연 자리로 돌린다 — 그 단추가 화면에 남아 있을 때만(다른 계약·목록으로 갔으면 둔다).
      const el = returnFocusRef.current;
      if (el && el.isConnected && typeof el.focus === 'function') el.focus();
    };
  }, []);

  const setField = (name, value) => setValues((prev) => ({ ...prev, [name]: value }));
  const changedFields = () => FIELDS.filter((name) => wireText(name, values[name]) !== wireText(name, initialRef.current[name]));
  const isDirty = () => changedFields().length > 0 || file !== null;
  const isRequired = (name) => name !== 'auto_renewal' && !(isEdit ? CLEARABLE : CREATE_OPTIONAL).has(name);
  const createBlocked = !isEdit && departmentsFailed;

  // 필수 칸 → 날짜 → 파일 순으로 보고 처음 걸린 하나만 돌려준다(서버도 칸 검사가 날짜보다 먼저).
  const validate = (changed) => {
    const checked = isEdit ? new Set(changed) : new Set(FIELDS);
    for (const name of FIELDS) {
      if (checked.has(name) && isRequired(name) && !wireText(name, values[name])) {
        return `${FIELD_LABELS[name]} 칸은 비울 수 없습니다.`;
      }
    }
    // YYYY-MM-DD 라 글자 순서 = 날짜 순서 · 두 칸 다 값이 있을 때만 · 수정은 짝 중 하나가 바뀐 칸일 때만.
    const start = wireText('start_date', values.start_date);
    const end = wireText('end_date', values.end_date);
    const notice = wireText('notice_date', values.notice_date);
    if ((checked.has('start_date') || checked.has('end_date')) && start && end && end < start) return DATE_ORDER;
    if ((checked.has('notice_date') || checked.has('end_date')) && notice && end && notice > end) return NOTICE_ORDER;
    if (!file) return isEdit ? '' : '원본 파일을 골라 주세요.';
    if (file.size > MAX_FILE_BYTES) return '파일이 20MB를 넘습니다.';
    if (!ALLOWED_EXT.includes(extensionOf(file.name))) return EXT_MESSAGE;
    return '';
  };

  const submit = async (event) => {
    event.preventDefault();
    if (savingRef.current || createBlocked) return;
    const changed = changedFields();
    const problem = validate(changed);
    if (problem) {
      setError(problem);
      return;
    }
    if (isEdit && changed.length === 0 && !file) {
      setError('바뀐 내용이 없습니다.');
      return;
    }

    const formData = new FormData();
    if (isEdit) {
      // 지울 수 있는 칸을 비우면 '' 가 간다(서버가 NULL).
      changed.forEach((name) => formData.append(name, wireText(name, values[name])));
    } else {
      FIELDS.forEach((name) => {
        const text = wireText(name, values[name]);
        if (CREATE_OPTIONAL.has(name) && !text) return;   // 빈 선택 칸은 키를 넣지 않는다
        formData.append(name, text);
      });
    }
    if (file) formData.append('file', file);

    savingRef.current = true;
    setSaving(true);
    setError('');
    try {
      const r = isEdit ? await contractsAPI.update(contract.id, formData) : await contractsAPI.create(formData);
      toast(isEdit ? '계약을 수정했습니다.' : '계약을 등록했습니다.');
      if (mountedRef.current) onSaved(r.data);
    } catch (err) {
      // nginx 본문 한도(413)는 JSON 이 아니다.
      const message = err?.response?.status === 413
        ? '파일이 너무 큽니다(20MB 까지).'
        : await errorText(err, '저장하지 못했습니다.');
      if (mountedRef.current) setError(message);
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  // 취소·바깥 클릭·Esc — 저장 중이면 무시 · 바뀐 것이 있으면 확인 · 확인 대기 중 들어온 요청은 무시.
  const requestClose = async () => {
    if (savingRef.current || confirmingRef.current) return;
    if (!isDirty()) {
      onClose();
      return;
    }
    confirmingRef.current = true;
    try {
      if (await confirmDialog('입력한 내용이 저장되지 않습니다. 닫을까요?')) onClose();
    } finally {
      confirmingRef.current = false;
    }
  };

  const labelFor = (name, text = FIELD_LABELS[name]) => (
    <label htmlFor={fieldId(name)} style={labelStyle}>
      {text}
      {(name === 'file' ? !isEdit : isRequired(name)) && <span aria-hidden="true" style={{ color: COLORS.dangerText }}> *</span>}
    </label>
  );

  const textField = (name, extra = {}) => (
    <div style={boxStyle}>
      {labelFor(name)}
      <input
        id={fieldId(name)}
        ref={name === 'contract_no' ? firstFieldRef : undefined}
        type="text"
        aria-label={FIELD_LABELS[name]}
        value={values[name]}
        maxLength={MAX_LEN[name]}
        onChange={(e) => setField(name, e.target.value)}
        style={inputStyle}
        {...extra}
      />
    </div>
  );

  const dateField = (name) => (
    <div style={boxStyle}>
      {labelFor(name)}
      <input
        id={fieldId(name)}
        type="date"
        aria-label={FIELD_LABELS[name]}
        value={values[name]}
        onChange={(e) => setField(name, e.target.value)}
        style={inputStyle}
      />
    </div>
  );

  // 선택 칸 — 처음 값이 빈 글자면 «선택», 선택지 밖 옛 값이면 맨 위에 «(목록에 없음)» 으로 남긴다(안 바꾸면 보내지 않는다).
  const selectField = (name, options, extraNote = null) => {
    const initial = initialRef.current[name];
    const listed = options.some(([value]) => value === initial);
    const outsideNote = name === 'department' && departmentsFailed ? '(지금 값)' : '(목록에 없음)';
    return (
      <div style={boxStyle}>
        {labelFor(name)}
        <select
          id={fieldId(name)}
          aria-label={FIELD_LABELS[name]}
          value={values[name]}
          onChange={(e) => setField(name, e.target.value)}
          style={inputStyle}
        >
          {initial === '' && <option value="">선택</option>}
          {initial !== '' && !listed && <option value={initial}>{`${initial} ${outsideNote}`}</option>}
          {options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
        </select>
        {extraNote}
      </div>
    );
  };
  const choiceOptions = (name) => Object.entries(CHOICES[name]).map(([code, label]) => [code, `${code} ${label}`]);
  const departmentOptions = (Array.isArray(departments) ? departments : [])
    .map((d) => d?.name)
    .filter((name) => typeof name === 'string' && name !== '')
    .filter((name, index, all) => all.indexOf(name) === index)
    .map((name) => [name, name]);
  // 등록 창에서 부서를 읽었는데 0개 — 부서는 필수라 저장이 막히기만 하니 어디서 추가하는지 알린다.
  //    수정 창은 지금 값이 선택지로 남으므로 알리지 않는다.
  const createNoDepartments = !isEdit && !departmentsFailed && departmentOptions.length === 0;

  const titleText = isEdit ? '계약 수정' : '계약 등록';

  return (
    <div
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) requestClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '20px', background: 'rgba(17,24,39,.45)',
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="contract-form-title"
        noValidate
        onSubmit={submit}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            requestClose();
          }
        }}
        style={{
          width: 'min(720px, 100%)', maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box',
          padding: '24px', borderRadius: RADIUS.lg, background: COLORS.surface, boxShadow: '0 18px 48px rgba(17,24,39,.2)',
        }}
      >
        <h2 id="contract-form-title" style={{ margin: '0 0 18px', color: COLORS.text, fontSize: '19px' }}>{titleText}</h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', columnGap: '12px' }}>
          {textField('contract_no', isEdit ? {} : { placeholder: '비우면 자동(예: B-001)' })}
          {selectField('category', choiceOptions('category'))}
          {selectField('region', choiceOptions('region'))}
          {selectField('party_type', choiceOptions('party_type'))}
          {selectField('term_type', choiceOptions('term_type'))}
          {textField('contract_name')}
          {textField('counterparty')}
          {textField('center')}
          {selectField('department', departmentOptions, createBlocked ? (
            <div style={{ ...noteStyle, color: COLORS.dangerText }}>{DEPARTMENTS_FAILED}</div>
          ) : createNoDepartments ? (
            <div style={{ ...noteStyle, color: COLORS.dangerText }}>{DEPARTMENTS_EMPTY}</div>
          ) : null)}
          {textField('owner_name')}
          {textField('sub_owner_name')}
          {dateField('start_date')}
          {dateField('end_date')}
          {dateField('notice_date')}
        </div>

        <div style={boxStyle}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: COLORS.text, fontSize: '13px', fontWeight: 700 }}>
            <input
              type="checkbox"
              aria-label={FIELD_LABELS.auto_renewal}
              checked={values.auto_renewal}
              onChange={(e) => setField('auto_renewal', e.target.checked)}
            />
            {FIELD_LABELS.auto_renewal}
          </label>
        </div>

        <div style={boxStyle}>
          {labelFor('summary')}
          <textarea
            id={fieldId('summary')}
            aria-label={FIELD_LABELS.summary}
            value={values.summary}
            maxLength={MAX_LEN.summary}
            rows={4}
            onChange={(e) => setField('summary', e.target.value)}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        <div style={boxStyle}>
          {labelFor('file', '원본 파일')}
          <input
            id={fieldId('file')}
            type="file"
            aria-label="원본 파일"
            accept={FILE_ACCEPT}
            onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
            style={{ fontSize: '13px' }}
          />
          {isEdit && <div style={noteStyle}>지금 파일: {contract?.file_name || '없음'}</div>}
          {isEdit && file && <div style={{ ...noteStyle, color: COLORS.warn }}>저장하면 원본이 바뀝니다(옛 파일은 이력에 남습니다)</div>}
        </div>

        {error && (
          <div role="alert" style={{ marginBottom: '14px', color: COLORS.dangerText, fontSize: '13px' }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button type="button" onClick={requestClose} disabled={saving} style={btn('secondary')}>취소</button>
          <button
            type="submit"
            disabled={saving || createBlocked}
            style={{ ...btn('primary'), opacity: saving || createBlocked ? 0.65 : 1 }}
          >
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
      </form>
    </div>
  );
}
